/** Product publish feedback; the parent owns selection and mutation state. */

// Local components
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import {
  BulkActionBar,
  BulkActionButton,
  CountedText,
  DismissProgressBar,
  FadeIn,
  preserveEdgeWhitespace,
  splitAroundCount,
  splitTrailingWord,
} from '@/components/ui/bulk-action-bar';

// Icons
import { LoadingSpinnerIcon } from '@icons/LoadingSpinnerIcon';
import { CancelTagIcon } from '@icons/CancelTagIcon';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';
import { CloseIcon } from '@icons/CloseIcon';

// Hooks and utilities
import { AnimatedCount } from '@/components/ui/AnimatedCount';
import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';
import { interpolateCount, pluralizeKey } from '@/react/utils/i18n';


const LOADING_ICON = <LoadingSpinnerIcon className="size-5 shrink-0 text-off-black/60" />;
const QUEUED_ICON = <CheckCircleIcon className="size-5 shrink-0 text-yield-positive" aria-hidden="true" />;
const ERROR_ICON = <CancelTagIcon className="shrink-0 text-chilli-red" />;


/**
 * Finite set of visual states the bar can be in. Parent drives the
 * transition — typically `selected → loading → queued → (unmount)` on
 * success, or `selected → loading → error → (unmount)` on failure, once
 * GRAIN-1520 wires a mutation.
 */
export type BulkPublishBarMode = 'selected' | 'loading' | 'queued' | 'error';

export interface BulkPublishBarProps {
  /** Number shown inside the pill. Parent should freeze this at submit
   *  time so the loading/queued copy keeps its count if the underlying
   *  selection mutates while the bar is on screen. */
  selectedCount: number;

  /** Which of the four visual states to render. See {@link BulkPublishBarMode}. */
  mode: BulkPublishBarMode;

  /** Fires when the Publish button is clicked (selected mode only). */
  onPublish: () => void;

  /** Fires when the user clicks the dismiss (×) button (queued mode only). */
  onDismissQueued?: () => void;

  /** Fires when the user clicks the dismiss (×) button (error mode only). */
  onDismissError?: () => void;

  /** Fires when the queued progress bar reaches 100% — parent should flip
   *  `mode` back to `selected` or unmount the bar. Respects hover pause
   *  and `prefers-reduced-motion`. Error mode has no auto-dismiss — the
   *  user must acknowledge the failure explicitly via the × button. */
  onAutoDismiss?: () => void;

  /** Fires when the underlined "queued" word is clicked. When provided,
   *  the word renders as a real `<button>`; otherwise it renders as a
   *  decorative underlined span. */
  onQueuedLinkClick?: () => void;

  /** Override the queued countdown duration for previews. */
  dismissDurationMs?: number;

  /** Extra classes merged onto the outer shell (positioning should be
   *  applied by the parent, not the bar). */
  className?: string;
}

export function BulkPublishBar({
  selectedCount,
  mode,
  onPublish,
  onDismissQueued,
  onDismissError,
  onAutoDismiss,
  onQueuedLinkClick,
  dismissDurationMs,
  className,
}: BulkPublishBarProps) {
  const { t } = useTranslation();
  const selectedTemplate = t(pluralizeKey('bulk_bar.products_selected', selectedCount));
  // aria-label is a plain string; interpolate %{count} so screen readers
  // announce the real number instead of the raw token.
  const ariaLabel = interpolateCount(
    mode === 'selected' ? selectedTemplate
    : mode === 'loading' ? t('bulk_bar.products_publishing')
    : mode === 'queued' ? t('bulk_bar.products_queued')
    : t('bulk_bar.products_error'),
    selectedCount,
  );

  return (
    <BulkActionBar ariaLabel={ariaLabel} className={className}>
      <FadeIn keyId={mode}>
        {mode === 'selected' && (
          <SelectedContent
            count={selectedCount}
            template={selectedTemplate}
            publishLabel={t('bulk_bar.publish')}
            onPublish={onPublish}
          />
        )}
        {mode === 'loading' && (
          <LoadingContent count={selectedCount} template={t('bulk_bar.products_publishing')} />
        )}
        {mode === 'queued' && (
          <QueuedContent
            count={selectedCount}
            template={t('bulk_bar.products_queued')}
            dismissLabel={t('bulk_bar.dismiss')}
            onDismiss={onDismissQueued}
            onLinkClick={onQueuedLinkClick}
          />
        )}
        {mode === 'error' && (
          <ErrorContent
            count={selectedCount}
            template={t('bulk_bar.products_error')}
            dismissLabel={t('bulk_bar.dismiss')}
            onDismiss={onDismissError}
          />
        )}
      </FadeIn>

      {mode === 'queued' && (
        <DismissProgressBar durationMs={dismissDurationMs} onComplete={onAutoDismiss} />
      )}
    </BulkActionBar>
  );
}

interface SelectedContentProps {
  count: number;
  template: string;
  publishLabel: string;
  onPublish: () => void;
}

function SelectedContent({ count, template, publishLabel, onPublish }: SelectedContentProps) {
  return (
    <div className="flex items-center gap-8">
      <CountedText count={count} template={template} />
      <BulkActionButton onClick={onPublish}>{publishLabel}</BulkActionButton>
    </div>
  );
}

function LoadingContent({ count, template }: { count: number; template: string }) {
  return (
    <span className="flex items-center gap-2">
      {LOADING_ICON}
      <CountedText count={count} template={template} />
    </span>
  );
}

interface QueuedContentProps {
  count: number;
  template: string;
  dismissLabel: string;
  onDismiss?: () => void;
  onLinkClick?: () => void;
}

function QueuedContent({ count, template, dismissLabel, onDismiss, onLinkClick }: QueuedContentProps) {
  const [pre, postRaw] = splitAroundCount(template);
  const [middle, suffix] = splitTrailingWord(postRaw);
  return (
    <div className="flex items-center gap-4">
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
        {QUEUED_ICON}
        <span className="inline-flex items-center">
          {pre && <span>{preserveEdgeWhitespace(pre)}</span>}
          <AnimatedCount value={count} />
          {middle && <span>{preserveEdgeWhitespace(middle)}</span>}
          {onLinkClick ? (
            <button
              type="button"
              onClick={onLinkClick}
              className={cn('cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', UNDERLINE_STATIC)}
            >
              {suffix}
            </button>
          ) : (
            <span className={cn('cursor-pointer', UNDERLINE_STATIC)}>{suffix}</span>
          )}
        </span>
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <CloseIcon className="size-3" />
        </button>
      )}
    </div>
  );
}

interface ErrorContentProps {
  count: number;
  template: string;
  dismissLabel: string;
  onDismiss?: () => void;
}

function ErrorContent({ count, template, dismissLabel, onDismiss }: ErrorContentProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex items-center gap-[6px]">
        {ERROR_ICON}
        <CountedText count={count} template={template} />
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <CloseIcon className="size-3" />
        </button>
      )}
    </div>
  );
}

